import React, { useState } from 'react';
import { Lock, Mail, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useSiteGate } from '@/context/SiteGateContext';

const SiteGate = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useSiteGate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((item) => item.msg || JSON.stringify(item)).join(' '));
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-byron-bg flex items-center justify-center px-4" data-testid="site-gate-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="/byron-logo-transparent-26.svg" alt="Byrōn Properties" className="h-10 mx-auto mb-4" />
          <p className="font-heading text-2xl text-[#1A1A1A] tracking-[0.04em] mb-2">
            Geneva &amp; Beyond
          </p>
          <p className="text-sm uppercase tracking-[0.2em] text-[#6B6B6B]">
            Private Preview
          </p>
        </div>

        <div className="bg-white rounded-sm shadow-sm p-8 md:p-10">
          <h2 className="font-heading text-xl text-[#1A1A1A] mb-2 text-center">Enter Access Code</h2>
          <p className="text-sm text-[#6B6B6B] text-center mb-6">
            This website is not yet published. Please sign in to continue.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-start gap-3" data-testid="site-gate-error">
              <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9B9B]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] transition-colors text-[#1A1A1A]"
                  placeholder="admin@byron-properties.ch"
                  required
                  data-testid="site-gate-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6B6B] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9B9B9B]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] transition-colors text-[#1A1A1A]"
                  placeholder="••••••••"
                  required
                  data-testid="site-gate-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9B9B9B] hover:text-[#6B6B6B]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#1A1A1A] text-white text-sm uppercase tracking-[0.15em] rounded-sm hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="site-gate-submit"
            >
              {loading ? 'Verifying...' : 'Enter Site'}
            </button>
          </form>
        </div>

        <p className="text-center mt-6 text-sm text-[#9B9B9B]">
          Authorized access only.
        </p>
      </div>
    </div>
  );
};

export default SiteGate;
